import { useState, useEffect } from 'react';
import { FeedbackPopup, FeedbackType } from 'src/components/feedback_popup';
import { Warning } from 'src/components/icons/warning';
import { RadioGroupField } from 'src/components/radio_group';
import { OrderStatus } from 'src/types';

enum SaveOrderWithReportOptionValue {
  RequestNewSpecimen = 'request_new_specimen',
  RequestRescan = 'request_rescan',
  RunAiOnExisting = 'run_ai_on_existing',
}

type SaveOrderWithReportOption = {
  value: SaveOrderWithReportOptionValue;
  status: OrderStatus;
  label: string;
};

const SAVE_ORDER_WITH_REPORT_OPTIONS: SaveOrderWithReportOption[] = [
  {
    value: SaveOrderWithReportOptionValue.RequestNewSpecimen,
    status: OrderStatus.LabProcessing,
    label: 'Request a new specimen from the pathology lab',
  },
  {
    value: SaveOrderWithReportOptionValue.RequestRescan,
    status: OrderStatus.LabProcessing,
    label: 'Request a rescan of the existing specimen',
  },
  {
    value: SaveOrderWithReportOptionValue.RunAiOnExisting,
    status: OrderStatus.AiProcessing,
    label: 'Run AI on the existing image',
  },
];

interface SaveOrderWithReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (revertStatus: OrderStatus) => Promise<void>;
}

export const SaveOrderWithReportDialog = ({
  open,
  onClose,
  onSubmit,
}: SaveOrderWithReportDialogProps) => {
  const [selectedValue, setSelectedValue] =
    useState<SaveOrderWithReportOptionValue>(
      SaveOrderWithReportOptionValue.RequestNewSpecimen,
    );

  useEffect(() => {
    if (open) {
      setSelectedValue(SAVE_ORDER_WITH_REPORT_OPTIONS[0].value);
    }
  }, [open]);

  const selectedOption = SAVE_ORDER_WITH_REPORT_OPTIONS.find(
    (opt) => opt.value === selectedValue,
  );

  const handleConfirm = async () => {
    if (!selectedOption) return;
    await onSubmit(selectedOption.status);
    onClose();
  };

  return (
    <FeedbackPopup
      data={{
        primaryButtonText: 'YES, I CONFIRM',
        secondaryButtonText: 'CANCEL',
        icon: Warning,
        type: FeedbackType.Primary,
        title: 'Confirm Save and Revert Status',
        subtitle: ' ',
      }}
      width='500px'
      minHeight='280px'
      isDialogOpen={open}
      onPrimaryButtonClick={handleConfirm}
      handleDialogClose={onClose}
      primaryButtonTestId='save-order-with-report-confirm-button'
    >
      <RadioGroupField
        value={selectedValue}
        options={SAVE_ORDER_WITH_REPORT_OPTIONS.map((option) => ({
          value: option.value,
          name: option.label,
        }))}
        onChange={(e) =>
          setSelectedValue(e.target.value as SaveOrderWithReportOptionValue)
        }
      />
    </FeedbackPopup>
  );
};
